// src/components/SearchableCountryCodeDropdown.jsx
import React, { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronDown } from 'lucide-react';

const COUNTRY_CODES = [
    { code: '+234', label: '🇳🇬 Nigeria' },
    { code: '+1', label: '🇺🇸 United States' },
    { code: '+44', label: '🇬🇧 United Kingdom' },
    { code: '+27', label: '🇿🇦 South Africa' },
    { code: '+91', label: '🇮🇳 India' },
    { code: '+49', label: '🇩🇪 Germany' },
    { code: '+33', label: '🇫🇷 France' },
    { code: '+81', label: '🇯🇵 Japan' },
    { code: '+86', label: '🇨🇳 China' },
    { code: '+55', label: '🇧🇷 Brazil' },
];

/**
 * A searchable dropdown component for selecting a country code.
 *
 * @param {object} props - Component props.
 * @param {string} props.value - The currently selected country code value (e.g., '+234').
 * @param {function} props.onChange - Handler called when a new code is selected.
 */
const SearchableCountryCodeDropdown = ({ value, onChange, error }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    const selectedCountry = COUNTRY_CODES.find(c => c.code === value) || COUNTRY_CODES[0];

    // Filter options based on search term
    const filteredCodes = useMemo(() => {
        if (!searchTerm) return COUNTRY_CODES;
        const lowerSearch = searchTerm.toLowerCase();
        return COUNTRY_CODES.filter(country =>
            country.code.includes(lowerSearch) || country.label.toLowerCase().includes(lowerSearch)
        );
    }, [searchTerm]);

    // Handler for selecting an option
    const handleSelect = (code) => {
        onChange({ target: { name: 'country_code', value: code } });
        setIsOpen(false);
        setSearchTerm(''); // Clear search term after selection
    };

    // Outside click logic (Simplified: relies on the main Input's outside click logic)
    // Note: For a fully robust solution, you would need useEffect/handleClickOutside here.
    // For this demonstration, we focus on the search/visual rhyme.

    const variants = {
        hidden: { opacity: 0, y: -5 },
        visible: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -5 },
    };

    // Style to visually rhyme with the Input component
    const triggerClasses = `
        flex items-center justify-between text-sm font-medium h-12 // Ensure height matches Input
        bg-gray-50 text-text-secondary border-r border-gray-medium 
        rounded-l-lg px-2 cursor-pointer transition-colors duration-150
        ${error ? 'border-r-gmail-red' : ''}
    `;

    return (
        <div className="relative z-50">
            {/* Trigger Button - Visually integrated with the main Input border */}
            <div
                ref={dropdownRef}
                onClick={() => setIsOpen(!isOpen)}
                className={triggerClasses}
                style={{ width: '130px' }} // Fixed width for clean alignment
            >
                <span className="truncate text-text-primary mr-1">{selectedCountry.label.split(' ')[0]} {selectedCountry.code}</span>
                <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={variants}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-60"
                        onBlur={() => setIsOpen(false)}
                    >
                        {/* Search Input */}
                        <div className="p-2 border-b border-gray-100">
                            <div className="relative">
                                <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search code or country"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md border border-gray-300 focus:ring-1 focus:ring-gmail-blue focus:border-gmail-blue focus:outline-none"
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()} // Prevent clicking search input from closing dropdown
                                />
                            </div>
                        </div>

                        {/* Filtered Options */}
                        <div className="max-h-60 overflow-y-auto">
                            {filteredCodes.length > 0 ? (
                                filteredCodes.map((country) => (
                                    <button
                                        key={country.code}
                                        type="button"
                                        onClick={() => handleSelect(country.code)}
                                        className={`block w-full text-left px-3 py-2 text-sm text-text-secondary hover:bg-gray-100 transition-colors ${country.code === value ? 'bg-gmail-blue text-white hover:bg-gmail-blue-dark' : ''}`}
                                    >
                                        {country.label} ({country.code})
                                    </button>
                                ))
                            ) : (
                                <p className="text-center text-sm text-gray-500 py-4">No results found.</p>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SearchableCountryCodeDropdown;